import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { EmptyState } from '../components/common/EmptyState';
import { getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification } from '../lib/api';
import { formatDateTime } from '../lib/utils';
import { Bell, BellOff, Check, CheckCheck, Trash2, Wrench, CalendarClock, FileWarning, Route } from 'lucide-react';
import { toast } from 'sonner';

const getNotificationIcon = (type) => {
    switch (type) {
        case 'maintenance_due':
            return <CalendarClock className="w-5 h-5 text-primary" />;
        case 'work_order':
            return <Wrench className="w-5 h-5 text-blue-500" />;
        case 'document_expired':
            return <FileWarning className="w-5 h-5 text-orange-500" />;
        case 'trip':
            return <Route className="w-5 h-5 text-green-500" />;
        default:
            return <Bell className="w-5 h-5 text-muted-foreground" />;
    }
};

export default function NotificationsPage() {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    useEffect(() => {
        fetchNotifications();
    }, [filter]);

    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const params = {};
            if (filter === 'unread') params.is_read = false;
            if (filter === 'read') params.is_read = true;
            const response = await getNotifications(params);
            setNotifications(response.data);
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
            toast.error('Gagal memuat notifikasi');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkRead = async (notification) => {
        try {
            await markNotificationRead(notification.id);
            setNotifications(notifications.map(n => 
                n.id === notification.id ? { ...n, is_read: true } : n
            ));
            toast.success('Notifikasi ditandai sudah dibaca');
        } catch (error) {
            console.error('Failed to mark notification as read:', error);
            toast.error('Gagal update notifikasi');
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await markAllNotificationsRead();
            setNotifications(notifications.map(n => ({ ...n, is_read: true })));
            toast.success('Semua notifikasi ditandai sudah dibaca');
        } catch (error) {
            console.error('Failed to mark all as read:', error);
            toast.error('Gagal update notifikasi');
        }
    };

    const handleDelete = async (notification) => {
        try {
            await deleteNotification(notification.id);
            setNotifications(notifications.filter(n => n.id !== notification.id));
            toast.success('Notifikasi dihapus');
        } catch (error) {
            console.error('Failed to delete notification:', error);
            toast.error('Gagal menghapus notifikasi');
        }
    };

    const unreadCount = notifications.filter(n => !n.is_read).length;

    return (
        <Layout title="Notifikasi">
            <div className="space-y-6" data-testid="notifications-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div className="flex items-center gap-4">
                        <div className="flex gap-2">
                            <Button
                                variant={filter === 'all' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilter('all')}
                                className="rounded-sm text-xs uppercase tracking-wide"
                                data-testid="filter-all"
                            >
                                Semua
                            </Button>
                            <Button
                                variant={filter === 'unread' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilter('unread')}
                                className="rounded-sm text-xs uppercase tracking-wide"
                                data-testid="filter-unread"
                            >
                                Belum Dibaca ({unreadCount})
                            </Button>
                            <Button
                                variant={filter === 'read' ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setFilter('read')}
                                className="rounded-sm text-xs uppercase tracking-wide"
                                data-testid="filter-read"
                            >
                                Sudah Dibaca
                            </Button>
                        </div>
                    </div>
                    {unreadCount > 0 && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleMarkAllRead}
                            className="rounded-sm text-xs uppercase tracking-wide"
                            data-testid="mark-all-read-btn"
                        >
                            <CheckCheck className="w-4 h-4 mr-2" />
                            Tandai Semua Dibaca
                        </Button>
                    )}
                </div>

                {/* Notifications List */}
                {loading ? (
                    <LoadingSpinner size="large" />
                ) : notifications.length > 0 ? (
                    <Card className="industrial-card">
                        <CardContent className="p-0">
                            <div className="divide-y divide-border">
                                {notifications.map((notification, index) => (
                                    <div
                                        key={notification.id || index}
                                        className={`flex items-start gap-4 p-4 transition-colors ${
                                            !notification.is_read ? 'bg-primary/5' : 'hover:bg-muted/50'
                                        }`}
                                        data-testid={`notification-${index}`}
                                    >
                                        <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${
                                            !notification.is_read ? 'bg-primary/10' : 'bg-muted'
                                        }`}>
                                            {getNotificationIcon(notification.tipe)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <p className={`font-medium ${!notification.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                                                        {notification.judul}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        {notification.pesan}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-2 font-mono">
                                                        {formatDateTime(notification.created_at)}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {!notification.is_read && (
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleMarkRead(notification)}
                                                            className="h-8 w-8"
                                                            title="Tandai sudah dibaca"
                                                            data-testid={`mark-read-${index}`}
                                                        >
                                                            <Check className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(notification)}
                                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                                        title="Hapus"
                                                        data-testid={`delete-notification-${index}`}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                        {!notification.is_read && (
                                            <div className="w-2 h-2 bg-primary rounded-full mt-2" />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <Card className="industrial-card">
                        <CardContent>
                            <EmptyState
                                icon={BellOff}
                                title="Tidak Ada Notifikasi"
                                description={filter === 'unread' ? 'Semua notifikasi sudah dibaca.' : 'Tidak ada notifikasi.'}
                            />
                        </CardContent>
                    </Card>
                )}
            </div>
        </Layout>
    );
}
